import {
  carrierFromServiceLevel,
  fetchTracking,
  TrackingRequest,
  TrackingResult
} from '@/services/shipmentTracking'
import { Shipment } from '@/store/modules/types/order'

interface CacheEntry {
  result: TrackingResult | null
  loading: boolean
}

/**
 * In-memory cache of tracking results keyed by tracking number.
 * Survives for the lifetime of the page — one API call per shipment per session.
 */
const cache = new Map<string, CacheEntry>()

export function getTrackingEntry(
  trackingNumber: string
): CacheEntry | undefined {
  return cache.get(trackingNumber)
}

/**
 * Pre-populate the cache with mock tracking results (for demo mode).
 */
export function seedTrackingCache(
  entries: Record<string, TrackingResult>
): void {
  for (const [key, result] of Object.entries(entries)) {
    cache.set(key, { result, loading: false })
  }
}

/**
 * Fetch tracking info for a shipment, using the cache to avoid duplicate calls.
 * Returns immediately if already cached.
 */
export async function fetchShipmentTracking(
  shipment: Shipment,
  accessToken: string
): Promise<TrackingResult> {
  if (!shipment.trackingNumber) {
    return { status: 'no_info', carrier: '', trackingUrl: shipment.trackingUrl }
  }
  const key = shipment.trackingNumber
  const existing = cache.get(key)
  if (existing?.result) return existing.result
  if (existing?.loading) {
    // Already in flight — wait for it, with a timeout to avoid hanging forever
    return new Promise((resolve) => {
      const timeout = 30_000
      const start = Date.now()
      const interval = setInterval(() => {
        const entry = cache.get(key)
        if (entry?.result) {
          clearInterval(interval)
          resolve(entry.result)
        } else if (Date.now() - start > timeout) {
          clearInterval(interval)
          resolve({ status: 'error', message: 'Tracking request timed out' })
        }
      }, 100)
    })
  }

  cache.set(key, { result: null, loading: true })

  const request: TrackingRequest = {
    carrier: carrierFromServiceLevel(shipment.carrierServiceLevel),
    orderNumber: shipment.orderNumber,
    trackingNumber: shipment.trackingNumber
  }

  let result: TrackingResult
  try {
    result = await fetchTracking(
      request,
      accessToken,
      shipment.shipTime,
      shipment.trackingUrl
    )
  } catch (e: unknown) {
    result = {
      status: 'error',
      message: e instanceof Error ? e.message : 'Tracking fetch failed'
    }
  }

  cache.set(key, { result, loading: false })
  return result
}
